import { useEffect, useRef, useState } from 'react';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { useNavigate, useParams } from 'react-router-dom';
import './document-center.css';
import type { DocPage, DocTab } from './types';
import { cardFaxPage } from './utils';
import {
  answerDocument,
  cardsByStatus,
  cardsToChargeCount,
  cardsToChargeTotal,
  correctDocument,
  faxSetUp,
  firstDocId,
  getCardSync,
  getChargedHistorySync,
  getCheckHistorySync,
  getDocSync,
  getDocsByGroup,
  getNeedsSorted,
  getFiledHistorySync,
  getMissingHistorySync,
  hasStatement,
  markCharged,
  resolveHeld,
  needsCountSync,
  markCouldntCharge,
  getScenarioSync,
  useDcVersion,
} from './store';
import { DC_CHECKS_COUNT, DC_CHECKS_NOT_IN_BANK } from './fixtures';
import { Header } from './components/Header';
import { DocList } from './components/DocList';
import { DocDetail } from './components/DocDetail';
import { CardDetail, CardsList } from './components/Cards';
import { Checks } from './components/Checks';
import { CouldntChargeDialog, FaxDialog, PageViewer, UploadDialog } from './components/dialogs';

interface Toast { id: number; msg: string }

function useIsDesktop(): boolean {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

type Dialog = 'fax' | 'upload' | 'page' | 'couldnt' | 'cardfax' | null;

export function DocumentCenter({ tab = 'documents' }: { tab?: DocTab }) {
  useDcVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id, cardId } = useParams();

  const [dialog, setDialog] = useState<Dialog>(null);
  const [cardFax, setCardFax] = useState<DocPage | null>(null);
  const [phase, setPhase] = useState<'idle' | 'working'>('idle');
  const [workLine, setWorkLine] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastSeq = useRef(0);

  const addToast = (msg: string) => {
    const tid = ++toastSeq.current;
    setToasts((t) => [...t, { id: tid, msg }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== tid)); }, 6000);
  };

  const needs = getNeedsSorted();
  const today = getDocsByGroup('today');
  const filedHistory = getFiledHistorySync();
  const selectedDoc = id ? getDocSync(id) : null;
  const selectedCard = cardId ? getCardSync(cardId) : null;

  // auto-open the first document on the bare Documents tab (desktop)
  useEffect(() => {
    if (tab === 'documents' && !id && isDesktop) {
      const first = firstDocId();
      if (first) void navigate(`/document-center/${first}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id, isDesktop]);

  // auto-open the newest filed document on the Filed documents pill (desktop)
  useEffect(() => {
    if (tab === 'filed' && !id && isDesktop) {
      const newest = [...filedHistory].sort((a, b) => (b.receivedIso ?? '').localeCompare(a.receivedIso ?? ''))[0];
      if (newest) void navigate(`/document-center/filed/${newest.id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id, isDesktop]);

  // auto-open first card on the bare Cards tab (desktop)
  useEffect(() => {
    if (tab === 'cards' && !cardId && isDesktop) {
      // ?scenario=couldnt-charge opens the card that couldn't be charged, so its warning card shows first.
      const first = (getScenarioSync() === 'couldnt-charge' ? cardsByStatus('couldnt_charge')[0] : undefined)
        ?? cardsByStatus('to_charge')[0] ?? cardsByStatus('couldnt_charge')[0] ?? cardsByStatus('charged')[0];
      if (first) void navigate(`/document-center/cards/${first.id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, cardId, isDesktop]);

  const header = (
    <Header
      tab={tab}
      todayCount={today.length}
      needsCount={needsCountSync()}
      filedCount={filedHistory.length}
      cardsToCharge={cardsToChargeCount()}
      cardsToChargeTotal={cardsToChargeTotal()}
      checksCount={DC_CHECKS_COUNT}
      checksNotInBank={DC_CHECKS_NOT_IN_BANK}
      faxSetUp={faxSetUp()}
      onSetUpFax={() => { setDialog('fax'); }}
      onUpload={() => { setDialog('upload'); }}
      tabTo={(t) => (t === 'cards' ? '/document-center/cards' : t === 'checks' ? '/document-center/checks' : t === 'filed' ? '/document-center/filed' : '/document-center')}
    />
  );

  const dialogs = (
    <>
      {dialog === 'fax' ? <FaxDialog onClose={() => { setDialog(null); }} onTest={() => { setDialog(null); addToast('ELVA sent a test fax. It should arrive in a minute.'); }} /> : null}
      {dialog === 'upload' ? <UploadDialog onClose={() => { setDialog(null); }} onUploaded={(n) => { setDialog(null); addToast(`Uploaded ${String(n)} file${n === 1 ? '' : 's'}. ELVA is reading ${n === 1 ? 'it' : 'them'}.`); }} /> : null}
      {dialog === 'page' && selectedDoc ? <PageViewer pages={selectedDoc.pages} title={selectedDoc.title} onClose={() => { setDialog(null); }} /> : null}
      {dialog === 'couldnt' && selectedCard ? (
        <CouldntChargeDialog
          payer={selectedCard.payer}
          onClose={() => { setDialog(null); }}
          onSend={(reason, note) => {
            markCouldntCharge(selectedCard.id, reason, note);
            setDialog(null);
            addToast(`Sent to ELVA. ELVA called ${selectedCard.payer} and asked it to pay another way.`);
          }}
        />
      ) : null}
      {dialog === 'cardfax' && cardFax ? <PageViewer pages={[cardFax]} title="The fax" onClose={() => { setDialog(null); }} /> : null}
      {renderToasts()}
    </>
  );

  // ---- Checks tab ----
  if (tab === 'checks') {
    return (
      <div className="dc dc-page">
        {header}
        <Checks
          rows={getCheckHistorySync()}
          missing={getMissingHistorySync()}
          hasStatement={hasStatement()}
          onUploadStatement={() => { void navigate('/posting/bank'); }}
          onOpenMissingHitl={() => { void navigate('/human-in-the-loop/HL-BLUEBONNET'); }}
        />
        {dialogs}
      </div>
    );
  }

  // ---- Virtual cards tab ----
  if (tab === 'cards') {
    const mobileClass = !isDesktop ? (cardId ? 'dc-two-detail' : 'dc-two-list') : '';
    let detail: React.ReactNode = null;
    if (cardId) {
      detail = selectedCard ? (
        <CardDetail
          key={selectedCard.id}
          card={selectedCard}
          onMarkCharged={(approval) => { markCharged(selectedCard.id, approval); addToast(`Marked ${selectedCard.payer}’s card as charged. ELVA posted the payment${selectedCard.claims.length === 1 ? '' : 's'}.`); }}
          onCouldntCharge={() => { setDialog('couldnt'); }}
          onOpenFax={() => { setCardFax(cardFaxPage(selectedCard)); setDialog('cardfax'); }}
          onAskEft={(payer) => { addToast(`ELVA will call ${payer} to ask for EFT.`); }}
          {...(!isDesktop ? { onBack: () => { void navigate('/document-center/cards'); } } : {})}
        />
      ) : (
        <NotFoundInPage as="main" thing="card" id={cardId} listLabel="virtual cards" listTo="/document-center/cards" />
      );
    }
    return (
      <div className={`dc dc-page ${mobileClass}`}>
        {header}
        <div className="dc-body">
          <CardsList
            toCharge={cardsByStatus('to_charge')}
            couldnt={cardsByStatus('couldnt_charge')}
            chargedHistory={getChargedHistorySync()}
            {...(cardId ? { selectedId: cardId } : {})}
            onSelect={(cid) => { void navigate(`/document-center/cards/${cid}`); }}
          />
          {detail}
        </div>
        {dialogs}
      </div>
    );
  }

  // ---- Documents tab ----
  const mobileClass = !isDesktop ? (id ? 'dc-two-detail' : 'dc-two-list') : '';
  const docBase = tab === 'filed' ? '/document-center/filed' : '/document-center';
  const runAnswer = (values: Record<string, string>) => {
    if (!selectedDoc) return;
    setPhase('working');
    void (async () => {
      for await (const ev of answerDocument(selectedDoc.id, values)) {
        if ('working' in ev) setWorkLine(ev.working);
      }
      setPhase('idle'); setWorkLine('');
      addToast('ELVA posted the EOB and closed the Human in the loop item.');
    })();
  };
  const runCorrect = (correction: string) => {
    if (!selectedDoc) return;
    setPhase('working');
    void (async () => {
      for await (const ev of correctDocument(selectedDoc.id, correction)) {
        if ('working' in ev) setWorkLine(ev.working);
      }
      setPhase('idle'); setWorkLine('');
      addToast('ELVA applied your correction and ran it again.');
    })();
  };

  let detail: React.ReactNode = null;
  if (id) {
    detail = selectedDoc ? (
      <DocDetail
        key={selectedDoc.id}
        doc={selectedDoc}
        phase={phase}
        workLine={workLine}
        onOpenPage={() => { setDialog('page'); }}
        onSave={runAnswer}
        onWait={() => { addToast(`ELVA will post it when Bluebonnet’s clean copy arrives.`); }}
        onCorrect={runCorrect}
        onHeld={(action) => {
          const name = selectedDoc.held?.sender ?? 'it';
          resolveHeld(selectedDoc.id, action);
          if (action === 'discard') {
            addToast('Discarded. ELVA did not read it.');
            void navigate(docBase);
          } else if (action === 'trust') {
            addToast(`ELVA is reading it. ${name} is now a trusted sender.`);
          } else {
            addToast('ELVA is reading it, this once.');
          }
        }}
        {...(!isDesktop ? { onBack: () => { void navigate(docBase); } } : {})}
      />
    ) : (
      <NotFoundInPage as="main" thing="document" id={id} listLabel={tab === 'filed' ? 'filed documents' : 'documents'} listTo={docBase} />
    );
  }

  return (
    <div className={`dc dc-page ${mobileClass}`}>
      {header}
      <div className="dc-body">
        <DocList
          view={tab === 'filed' ? 'filed' : 'queue'}
          needs={needs}
          today={today}
          history={filedHistory}
          {...(id ? { selectedId: id } : {})}
          onSelect={(did) => { void navigate(`${docBase}/${did}`); }}
        />
        {detail}
      </div>
      {dialogs}
    </div>
  );

  function renderToasts() {
    if (toasts.length === 0) return null;
    return (
      <div className="dc-toasts">
        {toasts.map((t) => <div key={t.id} className="dc-toast" role="status"><span>{t.msg}</span></div>)}
      </div>
    );
  }
}
